import type * as React from "react";
import { toast as sonnerToast } from "sonner";

/**
 * Compatibility shim over Sonner.
 *
 * The object-argument signature is kept deliberately: ~97 call sites raise toasts
 * as `toast({ variant, title, description })`, and preserving the shape means the
 * migration off the deprecated Radix toast touched none of them.
 */

export type ToastVariant = "default" | "destructive" | "success" | "error" | "warning" | "info" | "loading";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastArgs {
  variant?: ToastVariant;
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastAction;
  cancel?: ToastAction;
  /** Overrides the derived duration. `null` keeps the toast up until dismissed. */
  duration?: number | null;
}

type ToastStyle = React.CSSProperties & { "--toast-duration": string };

interface ToastApi {
  toast: typeof toast;
  dismiss: typeof sonnerToast.dismiss;
}

/**
 * `default` and `destructive` are the two variants the previous Radix toast
 * exposed. They are kept as aliases so existing call sites need no edit.
 */
const resolveVariant = (variant: ToastVariant): Exclude<ToastVariant, "default" | "destructive"> => {
  switch (variant) {
    case "default":
      return "success";
    case "destructive":
      return "error";
    case "success":
    case "error":
    case "warning":
    case "info":
    case "loading":
      return variant;
    default: {
      const _exhaustiveCheck: never = variant;
      throw new Error(`Unhandled toast variant: ${String(_exhaustiveCheck)}`);
    }
  }
};

const BASE_DURATION_MS = {
  success: 4000,
  error: 6000,
  warning: 6000,
  info: 4000,
} as const;

const DESCRIPTION_EXTRA_MS = 1000;
const ACTION_DURATION_MS = 8000;

/**
 * Longer messages and messages that ask something of the user need longer on
 * screen, so duration is derived rather than set at each call site.
 */
const deriveDuration = ({
  variant,
  hasDescription,
  hasAction,
}: {
  variant: Exclude<ToastVariant, "default" | "destructive">;
  hasDescription: boolean;
  hasAction: boolean;
}): number | null => {
  if (variant === "loading") return null;
  if (hasAction) return ACTION_DURATION_MS;
  return BASE_DURATION_MS[variant] + (hasDescription ? DESCRIPTION_EXTRA_MS : 0);
};

export const toast = ({
  variant = "default",
  title,
  description,
  action,
  cancel,
  duration,
}: ToastArgs): string | number => {
  const resolvedVariant = resolveVariant(variant);
  const resolvedDuration =
    duration === undefined
      ? deriveDuration({
          variant: resolvedVariant,
          hasDescription: Boolean(description),
          hasAction: Boolean(action),
        })
      : duration;

  const style: ToastStyle | undefined =
    resolvedDuration === null ? undefined : { "--toast-duration": `${resolvedDuration}ms` };

  // Fresh IDs isolate exit timers and restart the countdown for each notification.
  sonnerToast.dismiss();

  return sonnerToast[resolvedVariant](title, {
    description,
    action,
    cancel,
    // Sonner treats Infinity as "never auto-dismiss".
    duration: resolvedDuration ?? Infinity,
    // The countdown bar is CSS-driven, so it only renders when a finite duration
    // gives it something to count down.
    className: resolvedDuration === null ? undefined : "breedr-toast--countdown",
    style,
  });
};

export const useToast = (): ToastApi => ({
  toast,
  dismiss: sonnerToast.dismiss,
});
